import React, { useRef, useEffect } from 'react';
import styles from './ActionDialog.module.scss';
import useDictionary from 'lib/hooks/useDictionary';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { useDialogPositioning } from './useDialogPositioning';

export interface ActionDialogProps {
  id?: string;
  isOpen: boolean;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  position?: 'right' | 'bottom';
  children: React.ReactNode;
  className?: string;
  ariaLabelledBy?: string;
}

export function ActionDialog({
  id,
  isOpen,
  onClose,
  buttonRef,
  position = 'right',
  children,
  className,
  ariaLabelledBy,
}: ActionDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { getDictionaryValue } = useDictionary();

  const { dialogPosition } = useDialogPositioning({
    isOpen,
    buttonRef,
    position,
  });

  // Close dialog on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }

    return undefined;
  }, [isOpen, onClose]);

  if (!isOpen) return <></>;

  if (isOpen)
    return (
      <div
        ref={dialogRef}
        className={`${styles.actionDialog} ${className || ''}`}
        style={{
          top: `${dialogPosition.top}px`,
          left: `${dialogPosition.left}px`,
        }}
        role="menu"
        id={id}
        aria-labelledby={ariaLabelledBy}
      >
        <div className={styles.dialogContent}>
          {children}
          <CtaButton
            className={styles.closeButton}
            text={getDictionaryValue('Components.ActionToolBar.Close') || 'Close'}
            onClick={onClose}
            aria-label={getDictionaryValue('Components.ActionToolBar.Close') || 'Close'}
            ctaStyle="link"
            ctaVariant="brand"
          />
        </div>
      </div>
    );

  return <></>;
}
